import {
  List,
  Datagrid,
  FunctionField,
  TextInput,
  SelectInput,
  RaRecord,
  TopToolbar,
  FilterButton,
  CreateButton,
  ExportButton,
} from 'react-admin';
import { useHasPermission } from '../admin/PermissionGate';
import { useFeature } from '../admin/featureFlags/FeatureGate';
import { INTAKE_QUEUE_CHOICES, INTAKE_QUEUE_FILTER } from '../admin/intake/intakeApi';
import { FEATURE_INTAKE_DEPARTMENTS } from '../admin/intake/intakeSides';
import { ARCHIVE_FILTER_CHOICES, ARCHIVE_FILTER_KEY, FEATURE_PRE_ORDER_ARCHIVE, isArchived } from './preOrderArchiveApi';
import { DateTimeField } from '../shared/DateFields';
import { Chip, Stack } from '@mui/material';
import { preOrderStatusColor, preOrderStatusLabel } from '../shared/status';
import { companyLabel } from '../shared/Panel';
import { wallClockDateTime } from '../shared/clientTime';
import {
  ClampedText,
  CompanyChip,
  compactDatagridSx,
} from '../shared/listCells';

export const PRE_ORDER_STATUSES = [
  'NOWE',
  'W_WERYFIKACJI',
  'UZUPELNIANE',
  'ZATWIERDZONE',
  'ODRZUCONE',
];
const statusChoices = PRE_ORDER_STATUSES.map((id) => ({ id, name: preOrderStatusLabel(id) }));

export const PreOrderStatusChip = ({ record }: { record: RaRecord }) => {
  const s = (record.status as string) ?? 'NOWE';
  const missing = (record.missingFields as string[] | undefined) ?? [];
  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
      {/* Zarchiwizowane (25.10.2026, `preOrderArchive`) — widać je tylko z filtrem „Pokaż zarchiwizowane”. */}
      {isArchived(record) ? (
        <Chip size="small" color="default" variant="outlined" label="zarchiwizowane" data-testid="pre-order-archived-chip" />
      ) : null}
      <Chip size="small" color={preOrderStatusColor(s)} label={preOrderStatusLabel(s)} />
      <Chip
        size="small"
        variant="outlined"
        color={missing.length ? 'warning' : 'success'}
        label={missing.length ? `braki: ${missing.length}` : 'komplet'}
      />
    </Stack>
  );
};

const filters = [
  <TextInput source="q" label="Klient, miasto, towar" alwaysOn />,
  <SelectInput source="status" label="Status" choices={statusChoices} />,
];

/**
 * Kolejka działu przyjęcia zleceń (makieta back office v2, fala B, B1,
 * 21.10.2026; przełącznik `intakeDepartments`) — filtr i kolumna. Klucz
 * `intakeQueue` to kontrakt z API (react-admin go pamięta); kafelki pulpitu
 * przyjęcia zleceń prowadzą tu z tym samym filtrem.
 */
const intakeFilters = [
  ...filters,
  <SelectInput
    key={INTAKE_QUEUE_FILTER}
    source={INTAKE_QUEUE_FILTER}
    label="Kolejka działu"
    choices={INTAKE_QUEUE_CHOICES.map(({ id, name }) => ({ id, name }))}
    alwaysOn
  />,
];

/** Etap obiegu i kolejka wiersza — z API (`intake`), ta sama reguła co filtr. */
export const IntakeCell = ({ record }: { record: RaRecord }) => {
  const intake = record.intake as
    | { queueLabel: string | null; stateLabel: string | null; returnCount: number | null }
    | undefined;
  if (!intake) return <span>—</span>;
  return (
    <Stack direction="row" spacing={0.5} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }} data-testid="pre-order-intake">
      {intake.stateLabel ? <Chip size="small" variant="outlined" label={intake.stateLabel} /> : null}
      {intake.queueLabel ? <Chip size="small" color="info" variant="outlined" label={intake.queueLabel} /> : null}
      {intake.returnCount ? <Chip size="small" color="warning" variant="outlined" label={`zwroty: ${intake.returnCount}`} /> : null}
    </Stack>
  );
};

/**
 * Pasek nad listą jak domyślny react-admina, ale „Utwórz” tylko z przyjęciem
 * zleceń (01.10.2026): prezlecenie wpisuje CS albo Administracja, FTL i Trade
 * mają podgląd (Administracja §15). Bez uprawnienia przycisku nie ma wcale.
 */
export const PreOrderListActions = () => {
  const canIntake = useHasPermission('ORDER_INTAKE');
  return (
    <TopToolbar>
      <FilterButton />
      {canIntake && <CreateButton />}
      <ExportButton />
    </TopToolbar>
  );
};

/**
 * „Pokaż zarchiwizowane” (25.10.2026, decyzja użytkownika 24.10.2026, A6;
 * `preOrderArchive`): bez filtra lista nie ma zarchiwizowanych. Klucz
 * `archived` to kontrakt z API.
 */
const archiveFilter = (
  <SelectInput
    key={ARCHIVE_FILTER_KEY}
    source={ARCHIVE_FILTER_KEY}
    label="Zarchiwizowane"
    choices={ARCHIVE_FILTER_CHOICES}
    emptyText="Bez zarchiwizowanych"
    data-testid="pre-order-archived-filter"
  />
);

// Prezlecenia — bogaty rekord roboczy wyciągnięty z maila (W3). Jeden mail może
// dać kilka prezleceń (lista ładunków / długi wątek) — patrz kolumna „Zlecenia".
export const PreOrderList = () => {
  const intakeOn = useFeature(FEATURE_INTAKE_DEPARTMENTS);
  const archiveOn = useFeature(FEATURE_PRE_ORDER_ARCHIVE);
  const listFilters = intakeOn ? intakeFilters : filters;
  return (
  <List
    filters={archiveOn ? [...listFilters, archiveFilter] : listFilters}
    actions={<PreOrderListActions />}
    sort={{ field: 'createdAt', order: 'DESC' }}
    perPage={25}
  >
    {/* Długie teksty skracane z podpowiedzią, spółka jako znacznik z krajem,
        czas bez sekund — wiersz w jednej linii od 1366 px (28.09.2026). */}
    <Datagrid rowClick="show" bulkActionButtons={false} sx={compactDatagridSx}>
      <FunctionField label="Klient" render={(r: RaRecord) => <ClampedText text={r.clientName} maxWidth={{ xs: 160, xl: 260 }} />} />
      <FunctionField label="Relacja" render={(r: RaRecord) => <ClampedText text={r.relation} maxWidth={{ xs: 180, xl: 300 }} />} />
      <FunctionField label="Towar" render={(r: RaRecord) => <ClampedText text={r.cargo} maxWidth={{ xs: 160, xl: 260 }} />} />
      {/* Czas polski z zapisu, nie strefa przeglądarki (`shared/clientTime.ts`). */}
      <FunctionField
        label="Załadunek"
        sortBy="loadingDate"
        render={(r: RaRecord) => <span style={{ whiteSpace: 'nowrap' }}>{wallClockDateTime(r.loadingDate)}</span>}
      />
      <FunctionField label="Status" render={(r: RaRecord) => <PreOrderStatusChip record={r} />} />
      {intakeOn ? <FunctionField label="Obieg" render={(r: RaRecord) => <IntakeCell record={r} />} /> : null}
      <FunctionField label="Zlecenia" render={(r: RaRecord) => (r._count?.orders ?? 0)} />
      <FunctionField
        label="Spółka"
        render={(r: RaRecord) => (
          <CompanyChip
            name={r.company?.name ?? companyLabel(r.company?.viaCompanyId ?? undefined)}
            country={r.company?.country}
          />
        )}
      />
      <DateTimeField source="createdAt" label="Utworzono" />
    </Datagrid>
  </List>
  );
};
